import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {ArrowBack, DeleteOutline, MoreVert} from '@mui/icons-material';
import {FabricObject} from 'fabric';
import {getTableCapacity} from '../utils/fabric-factory';
import {getTableGroupLabel, getTableGroupMemberId, StoredTableGroup, TableGroupKind} from '../utils/table-groups';
import FloorPlanInlineNumberInput from './FloorPlanInlineNumberInput';

interface Props {
    elements: FabricObject[];
    tableGroups: StoredTableGroup[];
    selectedGroupTableIds: string[];
    onBack: () => void;
    onCreateTableGroup: (kind: TableGroupKind, options?: Pick<StoredTableGroup, 'min' | 'max'>) => void;
    onDeleteTableGroup: (groupId: string) => void;
    onUpdateTableGroup: (groupId: string, patch: Partial<Pick<StoredTableGroup, 'kind' | 'min' | 'max'>>) => void;
}

const GROUP_LIST_COLUMNS = {
    status: 12,
    min: 52,
    max: 52,
    action: 40,
    gap: 8,
};

const GROUP_LIST_TEMPLATE = `${GROUP_LIST_COLUMNS.status}px minmax(0, 1fr) ${GROUP_LIST_COLUMNS.min}px ${GROUP_LIST_COLUMNS.max}px ${GROUP_LIST_COLUMNS.action}px`;

const GROUP_COLORS: Record<TableGroupKind, string> = {
    combination: '#1976d2',
    notBookable: '#d32f2f',
};

const EditGroupsPanel: React.FC<Props> = ({
                                              elements,
                                              tableGroups,
                                              selectedGroupTableIds,
                                              onBack,
                                              onCreateTableGroup,
                                              onDeleteTableGroup,
                                              onUpdateTableGroup,
                                          }) => {
    const {t} = useTranslation();
    const [groupActionsMenu, setGroupActionsMenu] = useState<{
        anchorEl: HTMLButtonElement;
        groupId: string;
    } | null>(null);

    const handleOpenGroupActions = (event: React.MouseEvent<HTMLButtonElement>, groupId: string) => {
        event.stopPropagation();
        setGroupActionsMenu({anchorEl: event.currentTarget, groupId});
    };

    const handleCloseGroupActions = () => {
        setGroupActionsMenu(null);
    };

    const handleDeleteGroup = () => {
        if (!groupActionsMenu) return;

        const {groupId} = groupActionsMenu;
        handleCloseGroupActions();
        onDeleteTableGroup(groupId);
    };

    // Resolve the selected canvas tables into the display data the sidebar needs for labels and defaults.
    const tableMetaById = useMemo(() => {
        return elements.reduce<Record<string, { name: string; capacity: number }>>((acc, element) => {
            const memberId = getTableGroupMemberId(element.floorPlanItem);
            const name = element.floorPlanItem?.table?.resource?.name;
            const table = element.floorPlanItem?.table;
            if (memberId && name && table) {
                acc[memberId] = {
                    name,
                    capacity: getTableCapacity(table.seats, table.resource?.capacity ?? 0),
                };
            }
            return acc;
        }, {});
    }, [elements]);

    const selectedCapacity = selectedGroupTableIds.reduce(
        (sum, tableId) => sum + (tableMetaById[tableId]?.capacity ?? 0),
        0,
    );
    const selectionCount = selectedGroupTableIds.length;
    // New combinations start at the joined capacity, while the minimum is pushed above any single-table booking.
    const smallestSelectedCapacity = selectionCount >= 2
        ? Math.min(...selectedGroupTableIds.map((tableId) => tableMetaById[tableId]?.capacity ?? 0))
        : undefined;
    const defaultMin = selectionCount >= 2 && selectedCapacity > 0 && smallestSelectedCapacity != null
        ? selectedCapacity - smallestSelectedCapacity + 1
        : undefined;
    const defaultMax = selectionCount >= 2 && selectedCapacity > 0 ? selectedCapacity : undefined;

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Box sx={{p: 2, pb: 1, borderBottom: 1, borderColor: 'divider'}}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Tooltip title={t('Back to list')}>
                        <IconButton size="small" onClick={onBack}>
                            <ArrowBack/>
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h6" noWrap sx={{flex: 1}}>
                        {t('Table Combinations')}
                    </Typography>
                </Stack>
            </Box>

            <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
                <Stack spacing={2.5}>
                    <Box>
                        {/* Group creation always starts from the current multi-table canvas selection. */}
                        <Typography variant="body1" sx={{pb: 0.5, fontWeight: 600}}>
                            {t('Create Table Combination')}
                        </Typography>
                        <Box
                            sx={{
                                mb: 1.5,
                                px: 1.5,
                                py: 1.25,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Typography variant="body2" sx={{fontWeight: 600, mb: 1}}>
                                {t('How it works')}
                            </Typography>
                            <Stack spacing={0.75}>
                                <Stack direction="row" spacing={1}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{minWidth: 16, fontWeight: 600}}
                                    >
                                        1.
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('Select 2 or more tables by clicking on them on the canvas')}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{minWidth: 16, fontWeight: 600}}
                                    >
                                        2.
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('Create a Table Combination or mark them as Not Bookable')}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => onCreateTableGroup('combination', {min: defaultMin, max: defaultMax})}
                                disabled={selectionCount < 2}
                                sx={{textTransform: 'none'}}
                            >
                                {t('Create Combination')}
                            </Button>
                            <Button
                                fullWidth
                                color="error"
                                variant="outlined"
                                onClick={() => onCreateTableGroup('notBookable')}
                                disabled={selectionCount < 2}
                                sx={{textTransform: 'none'}}
                            >
                                {t('Create not bookable')}
                            </Button>
                        </Stack>
                    </Box>

                    <Box>
                        {tableGroups.length > 0 ? (
                            <>
                                {/* Existing groups stay editable here so the user can adjust rules without recreating them. */}
                                <Typography variant="body1" sx={{pb: 0.5, fontWeight: 600, mt: 2}}>
                                    {t('Manage Table Combinations')}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: GROUP_LIST_TEMPLATE,
                                        columnGap: `${GROUP_LIST_COLUMNS.gap}px`,
                                        alignItems: 'end',
                                        pb: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{gridColumn: '1 / 3', fontWeight: 500}}
                                    >
                                        {t('Table Combinations')} ({tableGroups.length})
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{textAlign: 'center'}}>
                                        {t('Min')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{textAlign: 'center'}}>
                                        {t('Max')}
                                    </Typography>
                                </Box>

                                <List dense disablePadding>
                                    {tableGroups.map((group) => {
                                        // Fall back to raw ids so older data still renders even if a table name is unavailable.
                                        const tableNames = group.tableIds
                                            .map((tableId) => tableMetaById[tableId]?.name ?? tableId)
                                            .join(', ');
                                        // Raising the minimum may need to raise max too so the stored range stays valid.
                                        const handleMinCommit = (nextMin: number | undefined) => {
                                            onUpdateTableGroup(group.id, {
                                                min: nextMin,
                                                max: group.max != null && nextMin != null && group.max < nextMin ? nextMin : group.max,
                                            });
                                        };
                                        // Lowering the maximum below min is clamped back up to the current minimum.
                                        const handleMaxCommit = (nextMax: number | undefined) => {
                                            onUpdateTableGroup(group.id, {
                                                min: group.min,
                                                max: nextMax != null && group.min != null && nextMax < group.min ? group.min : nextMax,
                                            });
                                        };

                                        return (
                                            <ListItem
                                                key={group.id}
                                                disablePadding
                                                sx={{borderTop: 1, borderColor: 'divider'}}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        display: 'grid',
                                                        gridTemplateColumns: GROUP_LIST_TEMPLATE,
                                                        columnGap: `${GROUP_LIST_COLUMNS.gap}px`,
                                                        alignItems: 'center',
                                                        minHeight: 68,
                                                        px: 1.5,
                                                        py: 1,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 6,
                                                            height: 40,
                                                            borderRadius: 2,
                                                            // Reuse the group kind color as a compact visual marker in the list.
                                                            bgcolor: GROUP_COLORS[group.kind],
                                                            mr: 1.5,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Box sx={{minWidth: 0}}>
                                                        <ListItemText
                                                            primary={t(getTableGroupLabel(group.kind), {
                                                                defaultValue: getTableGroupLabel(group.kind),
                                                            })}
                                                            secondary={tableNames}
                                                            primaryTypographyProps={{
                                                                variant: 'body2',
                                                                fontSize: '0.875rem',
                                                                fontWeight: 600,
                                                                noWrap: true,
                                                            }}
                                                            secondaryTypographyProps={{
                                                                variant: 'body2',
                                                                color: 'text.primary',
                                                                noWrap: true,
                                                            }}
                                                        />
                                                    </Box>
                                                    {group.kind === 'combination' ? (
                                                        <>
                                                            <FloorPlanInlineNumberInput
                                                                value={group.min}
                                                                onCommit={handleMinCommit}
                                                                min={0}
                                                                allowEmpty
                                                            />
                                                            <FloorPlanInlineNumberInput
                                                                value={group.max}
                                                                onCommit={handleMaxCommit}
                                                                min={0}
                                                                allowEmpty
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            {/* Non-bookable groups do not use capacity bounds, so those columns stay informational only. */}
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{textAlign: 'center'}}
                                                            >
                                                                {t('N/A')}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{textAlign: 'center'}}
                                                            >
                                                                {t('N/A')}
                                                            </Typography>
                                                        </>
                                                    )}
                                                    <Tooltip title={t('More')} disableInteractive>
                                                        <IconButton
                                                            id={`table-group-actions-${group.id}`}
                                                            size="small"
                                                            sx={{justifySelf: 'center'}}
                                                            aria-label={t('More actions for {{name}}', {
                                                                name: t(getTableGroupLabel(group.kind), {
                                                                    defaultValue: getTableGroupLabel(group.kind),
                                                                }),
                                                            })}
                                                            aria-haspopup="menu"
                                                            aria-controls={
                                                                groupActionsMenu?.groupId === group.id
                                                                    ? 'table-group-actions-menu'
                                                                    : undefined
                                                            }
                                                            aria-expanded={
                                                                groupActionsMenu?.groupId === group.id || undefined
                                                            }
                                                            onClick={(event) => handleOpenGroupActions(event, group.id)}
                                                        >
                                                            <MoreVert fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </>
                        ) : (
                            // Keep the panel stable before the first group exists.
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{px: 0.5, pt: 12, textAlign: 'center'}}
                            >
                                {t('Table combinations will be displayed here')}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Box>

            <Menu
                id="table-group-actions-menu"
                anchorEl={groupActionsMenu?.anchorEl}
                open={!!groupActionsMenu}
                onClose={handleCloseGroupActions}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                MenuListProps={{
                    'aria-labelledby': groupActionsMenu
                        ? `table-group-actions-${groupActionsMenu.groupId}`
                        : undefined,
                }}
            >
                {/* This removes only the stored group record; the underlying tables stay on the canvas. */}
                <MenuItem onClick={handleDeleteGroup} sx={{color: 'error.main'}}>
                    <ListItemIcon sx={{color: 'inherit'}}>
                        <DeleteOutline fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText primary={t('Delete')}/>
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default EditGroupsPanel;
